import React from 'react';
import { useMakeInfiniteQuery } from 'src/api/queries/useMakeInfiniteQuery';
import { requestClient } from 'src/lib/services/api/request-api';
import { authQueries } from 'src/api/queries';
import { ApiQueryObject } from 'src/lib/services/api-query-params';
import { render } from 'src/lib/utils';

type ApiResponse = Awaited<ReturnType<typeof requestClient.getActivities>>;

type QueryContextType = ReturnType<typeof useMakeInfiniteQuery<ApiResponse>>;

export const RequestActivitiesQueryContext = React.createContext<QueryContextType | undefined>(
  undefined,
);

const makeQueryKey = (id: string) => [authQueries.request, id, 'activity'];

export const RequestActivitiesQueryContextProvider = ({
  id,
  children,
  filters = {},
  options = {},
}: {
  id: string;
  children: React.ReactNode | ((query: QueryContextType) => React.ReactNode);
  filters?: ApiQueryObject;
  options?: Parameters<typeof useMakeInfiniteQuery<ApiResponse>>[0];
}) => {
  const query = useMakeInfiniteQuery<ApiResponse>(
    {
      queryKey: makeQueryKey(id),
      queryFn: ({ signal, ...context }) =>
        requestClient.getActivities(id, {
          signal,
          params: {
            ...filters,
            page: context.pageParam,
          },
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.meta.current_page < lastPage.meta.last_page
          ? lastPage.meta.current_page // current_page comes as a number of the next page from backend
          : undefined;
      },
      suspense: true,
      retry: false,
      staleTime: 60 * 1000,
      ...options,
    },
    filters,
  );

  return (
    <RequestActivitiesQueryContext.Provider value={query}>
      {render(children, query)}
    </RequestActivitiesQueryContext.Provider>
  );
};

export const useRequestActivitiesQueryContext = () => {
  const context = React.useContext(RequestActivitiesQueryContext);

  if (!context) {
    throw new Error(
      'useRequestActivitiesQueryContext must be used within a RequestActivitiesQueryContext',
    );
  }

  return context;
};

export const useRequestActivitiesReadonlyQueryContext = (id: string, filters?: ApiQueryObject) => {
  return useMakeInfiniteQuery<ApiResponse>(
    {
      queryKey: makeQueryKey(id),
      enabled: false,
    },
    filters,
  );
};
